/**
 * Primary app navigation — aligned with M4 design handoff `MODULES` (PRD Appendix).
 * Icons map to lucide-react (see handoff Section 4).
 */
import { type Permission } from '@/config/permissions';
import type { AccessSnapshot } from '@/types/access';
import { hasModuleAccess, hasPermission } from '@/utils/accessControl';
import type { UserRole } from '../types/role';

export type SidebarNavItem = {
  id: string;
  label: string;
  icon: string;
  path: string;
  tag?: string;
  /** Fallback when profile is loading or dev role override is active. */
  onClick?: () => void;
  allowedRoles?: readonly UserRole[];
  /** Module code from backend (e.g. M4). Used when RBAC profile is loaded. */
  moduleCode?: string;
  /** Extra gate beyond module access (e.g. user settings). */
  requiredPermission?: Permission;
  /** Feature flag key from the user profile; item is hidden unless the flag is true. */
  featureFlag?: string;
};

/** Order and copy match handoff `MODULES`; paths wired where the Vite app has routes. */

export const logoutSidebarNavItem: SidebarNavItem = {
  id: 'logout',
  label: 'Logout',
  icon: 'logout',
  path: '',
  allowedRoles: [
    'System Administrator',
    'Compliance Manager',
    'CISO',
    'Control Owner',
    'Internal Audit Lead',
    'Internal Audit Member',
    'External Auditor',
    'Employee',
  ],
};

export const moduleSidebarNavItems: readonly SidebarNavItem[] = [
  {
    id: 'm1',
    label: 'Compliance & Controls',
    icon: 'shieldCheck',
    path: '/compliance-controls',
    moduleCode: 'M1',
    allowedRoles: [
      'System Administrator',
      'Compliance Manager',
      'CISO',
      'Control Owner',
      'Internal Audit Lead',
      'Internal Audit Member',
      'External Auditor',
    ],
  },
  {
    id: 'm2',
    label: 'Audit Management',
    icon: 'magnifyingGlass',
    path: '/audit-management',
    moduleCode: 'M2',
    featureFlag: 'AUDIT_MANAGEMENT',
    allowedRoles: [
      'System Administrator',
      'Compliance Manager',
      'CISO',
      'Control Owner',
      'Internal Audit Lead',
      'Internal Audit Member',
      'External Auditor',
    ],
  },
  {
    id: 'm3',
    label: 'Risk Management',
    icon: 'warning',
    path: '/risk-management',
    moduleCode: 'M3',
    allowedRoles: [
      'Compliance Manager',
      'CISO',
      'Control Owner',
      'Internal Audit Lead',
      'External Auditor',
    ],
  },
  {
    id: 'm4',
    label: 'Policies & Documents',
    icon: 'files',
    path: '/documents',
    moduleCode: 'M4',
    allowedRoles: [
      'System Administrator',
      'Compliance Manager',
      'CISO',
      'Control Owner',
      'Internal Audit Lead',
      'Internal Audit Member',
      'External Auditor',
    ],
  },
  {
    id: 'm6',
    label: 'Evidence Management',
    icon: 'briefcase',
    path: '/evidence-management',
    moduleCode: 'M6',
    allowedRoles: [
      'System Administrator',
      'Compliance Manager',
      'CISO',
      'Control Owner',
      'External Auditor',
    ],
  },
];

export const settingsSidebarNavItem: SidebarNavItem = {
  id: 'settings',
  label: 'User Management',
  icon: 'usersThree',
  path: '/user-settings',
  allowedRoles: ['System Administrator'],
};

export const SIDEBAR_NAV_ITEMS: readonly SidebarNavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'squaresFour', path: '/dashboard' },
] as const;

export const isSidebarNavItemVisibleForRole = (item: SidebarNavItem, role: UserRole): boolean => {
  if (!item.allowedRoles?.length) return true;
  return item.allowedRoles.includes(role);
};

export const isSidebarNavItemVisible = (
  item: SidebarNavItem,
  options: {
    role: UserRole;
    snapshot: AccessSnapshot;
    useRoleFallback: boolean;
    featureFlags?: Record<string, boolean>;
  }
): boolean => {
  if (!isSidebarNavItemVisibleForRole(item, options.role)) {
    return false;
  }

  if (item.featureFlag && options.featureFlags?.[item.featureFlag] !== true) {
    return false;
  }

  if (options.useRoleFallback) {
    return true;
  }

  if (item.moduleCode && !hasModuleAccess(options.snapshot, item.moduleCode)) {
    return false;
  }
  if (item.requiredPermission && !hasPermission(options.snapshot, item.requiredPermission)) {
    return false;
  }

  return true;
};

export const getSidebarNavItemsForRole = (role: UserRole): readonly SidebarNavItem[] => {
  return SIDEBAR_NAV_ITEMS.filter((item) => isSidebarNavItemVisibleForRole(item, role));
};

export const getSidebarNavItemsForAccess = (options: {
  role: UserRole;
  snapshot: AccessSnapshot;
  useRoleFallback: boolean;
}): readonly SidebarNavItem[] => {
  return SIDEBAR_NAV_ITEMS.filter((item) => isSidebarNavItemVisible(item, options));
};
